import { useState, useRef, useCallback, useEffect } from "react";
import { toPng, toBlob, getFontEmbedCSS } from "html-to-image";
import { Download, Share2, ArrowLeft, AlertTriangle, Maximize2, X, HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MultiShelterShareCard, CARD_WIDTH, CARD_HEIGHT_POST, CARD_HEIGHT_STORY } from "@/components/MultiShelterShareCard";
import shareCardBg from "@/assets/share-card-bg.png";
import { ShelterSelectionStep } from "./ShelterSelectionStep";
import { useShelters } from "@/hooks/useShelters";
import { FAQModal } from "./FAQModal";
import { useConfetti } from "@/hooks/useConfetti";
import { saveGraphicDownload } from "@/hooks/useGraphicDownloads";
import { dataUrlToPngBlobChunked, waitForImages } from "@/components/admin/instagram/exportUtils";
import { imageToDataUrl } from "@/hooks/useImageDataUrl";
import { composePngOnBackground } from "@/lib/export/compositePng";
import type { Shelter } from "@/types/shelter";

interface ShareNeedsModalProps {
  shelter: Shelter;
  variant?: "button" | "icon";
  controlledOpen?: boolean;
  onControlledOpenChange?: (open: boolean) => void;
}

// Map of shelter ID to edited needs text
type EditedNeedsMap = Record<string, string>;

// Map of shelter ID to manual urgent override (true = force urgent, false = force not urgent, undefined = use original)
type UrgentOverrideMap = Record<string, boolean | undefined>;

const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
};

type Step = "select" | "preview";

type SizeMode = "post" | "story" | "compact";

type NeedsFontSize = "small" | "medium" | "large";

interface IosSaveState {
  filename: string;
  file: File;
  blobUrl: string;
  shareSupported: boolean;
}

export const ShareNeedsModal = ({ shelter, variant = "button", controlledOpen, onControlledOpenChange }: ShareNeedsModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use controlled or uncontrolled mode
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = isControlled ? (onControlledOpenChange || (() => {})) : setInternalOpen;
  const [step, setStep] = useState<Step>("select");
  const [selectedShelters, setSelectedShelters] = useState<Shelter[]>([shelter]);
  const [editedNeeds, setEditedNeeds] = useState<EditedNeedsMap>({});
  const [urgentOverrides, setUrgentOverrides] = useState<UrgentOverrideMap>({});
  const [hideDisclaimer, setHideDisclaimer] = useState(false);
  const [customHeadline, setCustomHeadline] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewScale, setPreviewScale] = useState(0.45);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenScale, setFullscreenScale] = useState(0.8);
  const [sizeMode, setSizeMode] = useState<SizeMode>("post");
  const [needsFontSize, setNeedsFontSize] = useState<NeedsFontSize>("medium");
  const [compactHeight, setCompactHeight] = useState(CARD_HEIGHT_POST);
  const [iosSave, setIosSave] = useState<IosSaveState | null>(null);
  const [bgDataUrl, setBgDataUrl] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const exportCardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: allShelters = [], isLoading: isLoadingShelters } = useShelters();
  const { fireConfetti } = useConfetti();

  // Pre-convert background image to DataURL.
  // This is critical for iOS/Android exports where html-to-image can drop regular URL images.
  useEffect(() => {
    let cancelled = false;
    imageToDataUrl(shareCardBg)
      .then((url) => {
        if (!cancelled) setBgDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBgDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isIOSDevice = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroidDevice = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
  const isMobileDevice =
    (typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) ||
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 767px)").matches);

  // Reset state only when opening.
  // IMPORTANT: don't depend on the whole `shelter` object here, because it may get
  // a new reference during data refresh and would reset the editor while the user types.
  useEffect(() => {
    if (!isOpen) return;
    setStep("select");
    setSelectedShelters([shelter]);
    setEditedNeeds({});
    setUrgentOverrides({});
    setHideDisclaimer(false);
    setCustomHeadline("");
    setSizeMode("post");
    setNeedsFontSize("medium");
    setIosSave(null);
  }, [isOpen]);

  // Initialize edited needs when moving to preview step
  // Only run when step changes to preview, not when editedNeeds changes
  useEffect(() => {
    if (step === "preview") {
      setEditedNeeds((prev) => {
        const initialNeeds: EditedNeedsMap = { ...prev };
        let hasChanges = false;
        selectedShelters.forEach((s) => {
          if (!(s.id in initialNeeds)) {
            initialNeeds[s.id] = s.current_needs || "";
            hasChanges = true;
          }
        });
        return hasChanges ? initialNeeds : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedShelters]);

  // Create shelters with edited needs and urgent overrides for the card
  const sheltersWithEditedNeeds = selectedShelters.map((s) => ({
    ...s,
    current_needs: editedNeeds[s.id] ?? s.current_needs,
    // Apply urgent override if set, otherwise keep original
    urgent_needs: urgentOverrides[s.id] !== undefined ? urgentOverrides[s.id] : s.urgent_needs,
  }));

  // Get actual card height based on mode
  const getFixedHeight = () => {
    if (sizeMode === "story") return CARD_HEIGHT_STORY;
    return CARD_HEIGHT_POST;
  };
  const cardHeight = sizeMode === "compact" ? compactHeight : getFixedHeight();

  // Measure compact card height
  useEffect(() => {
    if (sizeMode === "compact" && cardRef.current) {
      const measureHeight = () => {
        if (cardRef.current) {
          setCompactHeight(cardRef.current.offsetHeight);
        }
      };
      // Initial measure
      measureHeight();
      // Observe for changes
      const observer = new ResizeObserver(measureHeight);
      observer.observe(cardRef.current);
      return () => observer.disconnect();
    }
  }, [sizeMode, sheltersWithEditedNeeds, hideDisclaimer, customHeadline, needsFontSize]);

  const handleNeedsChange = (shelterId: string, value: string) => {
    setEditedNeeds((prev) => ({ ...prev, [shelterId]: value }));
  };

  const handleUrgentToggle = (shelterId: string) => {
    setUrgentOverrides((prev) => {
      const currentValue = prev[shelterId];
      const shelter = selectedShelters.find((s) => s.id === shelterId);
      const originalUrgent = shelter?.urgent_needs ?? false;
      
      // If currently undefined, set to opposite of original
      // If currently true/false, toggle
      const newValue = currentValue === undefined ? !originalUrgent : !currentValue;
      return { ...prev, [shelterId]: newValue };
    });
  };

  // Calculate scale based on container width and max height for vertical format
  useEffect(() => {
    const updateScale = () => {
      const currentHeight = sizeMode === "story" ? CARD_HEIGHT_STORY : 
                           sizeMode === "compact" ? compactHeight : CARD_HEIGHT_POST;
      
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth - 16;
        const maxHeight = window.innerHeight * 0.4; // max 40% of viewport height
        const scaleByWidth = containerWidth / CARD_WIDTH;
        const scaleByHeight = maxHeight / currentHeight;
        setPreviewScale(Math.min(scaleByWidth, scaleByHeight, 0.45));
      }
      
      // Fullscreen scale
      const screenWidth = window.innerWidth - 48;
      const screenHeight = window.innerHeight - 100;
      const scaleByWidth = screenWidth / CARD_WIDTH;
      const scaleByHeight = screenHeight / currentHeight;
      setFullscreenScale(Math.min(scaleByWidth, scaleByHeight, 1));
    };

    if (isOpen && step === "preview") {
      setTimeout(updateScale, 50);
    }

    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [isOpen, step, sizeMode, compactHeight]);

  const getFilename = useCallback(() => {
    if (selectedShelters.length === 1) {
      return `gdzieSchronisko_${createSlug(selectedShelters[0].name)}.png`;
    }
    return `gdzieSchronisko_${selectedShelters.length}_schronisk.png`;
  }, [selectedShelters]);

  const nextPaint = async () => {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  };

  const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let timeoutId: number | null = null;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    });

    try {
      return (await Promise.race([promise, timeoutPromise])) as T;
    } finally {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    }
  };

  const handleDownloadPng = useCallback(async () => {
    // Use a dedicated, unscaled export render on mobile to avoid iOS Safari dropping layers/images.
    const exportNode = (isMobileDevice ? exportCardRef.current : cardRef.current) || cardRef.current;
    if (!exportNode) return;

    setIsGenerating(true);
    let restoreRootBg: (() => void) | null = null;
    try {

      // Yield to the browser so iOS can repaint the "Generowanie..." state
      await nextPaint();

      // Ensure background is available as a DataURL before exporting.
      // On iOS Safari html-to-image can drop non-data-url <img> sources during rasterization.
      if (!bgDataUrl) {
        try {
          const url = await imageToDataUrl(shareCardBg);
          setBgDataUrl(url);
          // Give React a frame to re-render the card with the new background
          await nextPaint();
          await new Promise((resolve) => setTimeout(resolve, 50));
        } catch {
          // If it fails we still try export (fallback is original URL)
        }
      }

      // For compact mode, measure actual height
      const actualHeight = sizeMode === "compact" 
        ? exportNode.offsetHeight 
        : cardHeight;

      // iOS: the graphic root often has a fallback solid background (green) which can cover
      // the composed background. Force it transparent during the foreground capture.
      if (isMobileDevice) {
        const graphicRoot = exportNode.firstElementChild as HTMLElement | null;
        if (graphicRoot) {
          const prevValue = graphicRoot.style.getPropertyValue("background-color");
          const prevPriority = graphicRoot.style.getPropertyPriority("background-color");
          graphicRoot.style.setProperty("background-color", "transparent", "important");
          restoreRootBg = () => {
            if (prevValue) graphicRoot.style.setProperty("background-color", prevValue, prevPriority);
            else graphicRoot.style.removeProperty("background-color");
          };
        }
      }

      // Ensure fonts are loaded
      if (document.fonts?.load) {
        await Promise.all([
          document.fonts.load('400 16px "Nunito"'),
          document.fonts.load('700 16px "Nunito"'),
          document.fonts.load('800 16px "Nunito"'),
        ]);
      }
      await document.fonts.ready;
      
      // Wait for images and layout to settle (especially on mobile)
      await withTimeout(waitForImages(exportNode), 12000, "waitForImages");
      await new Promise((resolve) => setTimeout(resolve, isMobileDevice ? 500 : 100));
      await nextPaint();

      // Get font embed CSS
      let fontEmbedCSS = "";
      try {
        fontEmbedCSS = await getFontEmbedCSS(exportNode);
      } catch (e) {
        console.warn("[export] getFontEmbedCSS failed:", e);
      }

      const baseOptions = {
        width: CARD_WIDTH,
        height: actualHeight,
        pixelRatio: isMobileDevice ? 1 : 1,
        cacheBust: true,
        skipFonts: false,
        fontEmbedCSS,
        backgroundColor: "white",
      };

      const foregroundOptions = isMobileDevice
        ? {
            ...baseOptions,
            // IMPORTANT: keep transparency so we can composite on top of the background reliably.
            backgroundColor: "transparent",
            style: { backgroundColor: "transparent" },
            // Exclude background <img> from the DOM snapshot (we'll draw it manually on canvas).
            filter: (node: unknown) => {
              const el = node as HTMLElement | null;
              if (!el || typeof (el as any).getAttribute !== "function") return true;
              return el.getAttribute("data-export-bg") !== "true";
            },
          }
        : baseOptions;

      // Try toBlob first (more reliable on mobile), fallback to toPng
      let blob: Blob | null = null;
      const maxAttempts = isMobileDevice ? 3 : 1;
      
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
            await nextPaint();
            await withTimeout(waitForImages(exportNode), 12000, `waitForImages_retry_${attempt}`);
          }
          blob = await withTimeout(
            toBlob(exportNode, foregroundOptions as any),
            isMobileDevice ? 20000 : 15000,
            `toBlob_attempt_${attempt}`
          );
          if (blob && blob.size > 1000) break;
        } catch {
          // retry
        }
      }

      // Fallback to PNG data URL conversion
      if (!blob) {
        const dataUrl = await toPng(exportNode, foregroundOptions as any);
        blob = dataUrlToPngBlobChunked(dataUrl);
      }

      // Mobile: force background by compositing on canvas.
      if (isMobileDevice && blob) {
        try {
          const bgSrc = bgDataUrl || (await imageToDataUrl(shareCardBg));
          blob = await composePngOnBackground({
            width: CARD_WIDTH,
            height: actualHeight,
            backgroundSrc: bgSrc,
            foregroundBlob: blob,
          });
        } catch (e) {
          console.warn("[export] background compose failed, using foreground only:", e);
        }
      }

      if (!blob) throw new Error("Export failed: blob is null");
      
      
      const filename = getFilename();
      const file = new File([blob], filename, { type: blob.type || "image/png" });

      // iOS Safari: show dialog with share/copy/open options
      if (isIOSDevice) {
        
        const blobUrl = URL.createObjectURL(blob);
        let shareSupported = false;
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const navAny = navigator as any;
          shareSupported =
            typeof navAny.share === "function" &&
            (typeof navAny.canShare !== "function" || navAny.canShare({ files: [file] }) === true);
        } catch {
          shareSupported = false;
        }

        setIosSave({ filename, file, blobUrl, shareSupported });
        
        // Save download info to database
        for (const s of selectedShelters) {
          const needsText = editedNeeds[s.id] ?? s.current_needs ?? "";
          await saveGraphicDownload(s.id, s.name, needsText, sizeMode);
        }
        
        fireConfetti();
        toast({
          title: "Grafika gotowa 🎉",
          description: shareSupported 
            ? "Wybierz Udostępnij → Zapisz obraz" 
            : "Przytrzymaj obraz i wybierz 'Zapisz obraz'",
        });
        return;
      }

      // Android: direct download to Downloads folder
      if (isAndroidDevice) {
        
        
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = filename;
        link.href = blobUrl;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
        
        
        // Save download info
        for (const s of selectedShelters) {
          const needsText = editedNeeds[s.id] ?? s.current_needs ?? "";
          await saveGraphicDownload(s.id, s.name, needsText, sizeMode);
        }
        
        fireConfetti();
        toast({
          title: "Pobrano grafikę 🎉",
          description: "Sprawdź folder Pobrane na swoim telefonie.",
        });
        return;
      }

      // Other mobile: try native share (kept for potential future compatibility)
      if (isMobileDevice && navigator.share) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const canShareFiles = (navigator as any).canShare ? (navigator as any).canShare({ files: [file] }) : true;
          if (canShareFiles) {
            await navigator.share({ files: [file], title: filename });
            
            // Save download info
            for (const s of selectedShelters) {
              const needsText = editedNeeds[s.id] ?? s.current_needs ?? "";
              await saveGraphicDownload(s.id, s.name, needsText, sizeMode);
            }
            
            fireConfetti();
            toast({
              title: "Udostępniono grafikę 🎉",
              description: "Teraz możesz ją udostępnić na social media.",
            });
            return;
          }
        } catch (shareError) {
          
          // fallback below
        }
      }

      // Desktop/fallback: classic download
      
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = filename;
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      // Save download info to database for each shelter
      for (const s of selectedShelters) {
        const needsText = editedNeeds[s.id] ?? s.current_needs ?? "";
        await saveGraphicDownload(s.id, s.name, needsText, sizeMode);
      }

      // Fire confetti celebration!
      fireConfetti();

      toast({
        title: "Pobrano grafikę 🎉",
        description: "Teraz możesz ją udostępnić na Facebooku lub Instagramie.",
      });
    } catch (error) {
      console.error("[needs-export] ERROR", error);
      toast({
        title: "Błąd",
        description: "Nie udało się wygenerować grafiki. Spróbuj ponownie.",
        variant: "destructive",
      });
    } finally {
      restoreRootBg?.();
      setIsGenerating(false);
    }
  }, [getFilename, sizeMode, cardHeight, fireConfetti, selectedShelters, editedNeeds, isIOSDevice, isMobileDevice, isAndroidDevice, bgDataUrl]);

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(true);
  };

  const handleBack = () => {
    setStep("select");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {/* Only render triggers in uncontrolled mode */}
      {!isControlled && (
        variant === "icon" ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleIconClick}
                  className="p-1.5 rounded-full hover:bg-primary/10 transition-colors text-muted-foreground hover:text-primary"
                  aria-label="Udostępnij potrzeby"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Udostępnij potrzeby</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center justify-center gap-1.5 flex-1 px-3 py-2 rounded-lg text-sm font-semibold"
            >
              <Share2 className="w-4 h-4" />
              Udostępnij potrzeby
            </Button>
          </DialogTrigger>
        )
      )}
      <DialogContent 
        className="max-w-[100vw] w-[100vw] max-h-[100dvh] h-[100dvh] fixed top-0 left-0 translate-x-0 translate-y-0 rounded-none overflow-y-auto z-[2100] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {step === "select" ? (
          <div className="flex flex-col h-full">
            <DialogHeader className="pb-2 shrink-0">
              <DialogTitle>Wybierz schroniska</DialogTitle>
              <DialogDescription>
                Wybierz do 3 schronisk, których potrzeby chcesz udostępnić.
              </DialogDescription>
            </DialogHeader>

            <ShelterSelectionStep
              shelters={allShelters}
              selectedShelters={selectedShelters}
              onSelectionChange={setSelectedShelters}
              onNext={() => setStep("preview")}
              isLoading={isLoadingShelters}
              initialShelterId={shelter.id}
            />
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-8 w-8 p-0"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground">Wróć</span>
              </div>
              <DialogTitle className="text-center">Grafika do udostępnienia</DialogTitle>
              <DialogDescription className="text-center">
                {selectedShelters.length === 1
                  ? "Edytuj potrzeby i pobierz grafikę."
                  : `Edytuj potrzeby ${selectedShelters.length} schronisk i pobierz grafikę.`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Size selector */}
              <div className="flex gap-1.5 justify-center flex-wrap">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={sizeMode === "post" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSizeMode("post")}
                        className="text-xs px-3"
                      >
                        Post (4:5)
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Rozmiar najlepszy dla postów na Facebooku i Instagramie</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={sizeMode === "story" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSizeMode("story")}
                        className="text-xs px-3"
                      >
                        Story (9:16)
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Rozmiar najlepszy dla Instagram Stories</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={sizeMode === "compact" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSizeMode("compact")}
                        className="text-xs px-3"
                      >
                        Kompaktowy
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Rozmiar dopasowany do długości tekstu</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Card Preview (scaled) */}
              <div ref={containerRef} className="bg-muted/50 rounded-lg p-2 overflow-hidden flex justify-center relative">
                <button
                  onClick={() => setIsFullscreen(true)}
                  className="absolute top-3 right-3 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors"
                  aria-label="Powiększ podgląd"
                >
                  <Maximize2 className="w-4 h-4 text-muted-foreground" />
                </button>
                <div
                  className="relative overflow-hidden rounded cursor-pointer"
                  style={{
                    width: CARD_WIDTH * previewScale,
                    height: cardHeight * previewScale,
                  }}
                  onClick={() => setIsFullscreen(true)}
                >
                  <div
                    className="absolute top-0 left-0 origin-top-left"
                    style={{
                      width: CARD_WIDTH,
                      height: sizeMode === "compact" ? "auto" : cardHeight,
                      transform: `scale(${previewScale})`,
                    }}
                  >
                    <MultiShelterShareCard
                      ref={cardRef}
                      shelters={sheltersWithEditedNeeds}
                      hideDisclaimer={hideDisclaimer}
                      customHeadline={customHeadline}
                      sizeMode={sizeMode}
                      needsFontSize={needsFontSize}
                      backgroundDataUrl={bgDataUrl || undefined}
                    />
                  </div>
                </div>
              </div>

              {/* Hidden export render (full size, no scaling). Critical for iOS/Android export reliability. */}
              <div
                aria-hidden
                className="fixed left-0 top-0 pointer-events-none opacity-0"
                style={{
                  width: CARD_WIDTH,
                  height: sizeMode === "compact" ? "auto" : cardHeight,
                  zIndex: -1,
                }}
              >
                <MultiShelterShareCard
                  ref={exportCardRef}
                  shelters={sheltersWithEditedNeeds}
                  hideDisclaimer={hideDisclaimer}
                  customHeadline={customHeadline}
                  sizeMode={sizeMode}
                  needsFontSize={needsFontSize}
                  backgroundDataUrl={bgDataUrl || undefined}
                />
              </div>

              {/* Download button */}
              <Button
                onClick={handleDownloadPng}
                disabled={isGenerating}
                className="w-full gap-2"
                size="lg"
              >
                <Download className="w-4 h-4" />
                {isGenerating ? "Generowanie..." : "Pobierz PNG"}
              </Button>

              {/* Fullscreen Preview Modal */}
              {isFullscreen && (
                <div 
                  className="fixed inset-0 z-[1200] bg-black/90 flex items-center justify-center"
                  onClick={() => setIsFullscreen(false)}
                >
                  <button
                    onClick={() => setIsFullscreen(false)}
                    className="absolute top-4 right-4 p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                    aria-label="Zamknij podgląd"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                  <div
                    className="relative overflow-hidden rounded-lg shadow-2xl"
                    style={{
                      width: CARD_WIDTH * fullscreenScale,
                      height: cardHeight * fullscreenScale,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="absolute top-0 left-0 origin-top-left"
                      style={{
                        width: CARD_WIDTH,
                        height: sizeMode === "compact" ? "auto" : cardHeight,
                        transform: `scale(${fullscreenScale})`,
                      }}
                    >
                      <MultiShelterShareCard
                        shelters={sheltersWithEditedNeeds}
                        hideDisclaimer={hideDisclaimer}
                        customHeadline={customHeadline}
                        sizeMode={sizeMode}
                        needsFontSize={needsFontSize}
                        backgroundDataUrl={bgDataUrl || undefined}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Editable headline and needs section */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Nagłówek:</Label>
                  <Input
                    value={customHeadline}
                    onChange={(e) => setCustomHeadline(e.target.value)}
                    placeholder="Jak możesz pomóc?"
                    className="text-sm"
                  />
                </div>
                
                {selectedShelters.length > 1 ? (
                <div className="space-y-4">
                    {selectedShelters.map((s) => {
                      const isUrgentActive = urgentOverrides[s.id] !== undefined 
                        ? urgentOverrides[s.id] 
                        : s.urgent_needs;
                      
                      return (
                        <div key={s.id} className="space-y-2">
                          <div className="flex items-start justify-between gap-3">
                            <span className="text-sm font-semibold text-foreground leading-snug flex-1">
                              {s.name} ({s.city})
                            </span>
                            <label className="flex items-center gap-1.5 cursor-pointer shrink-0 mt-0.5">
                              <Checkbox
                                checked={isUrgentActive ?? false}
                                onCheckedChange={() => handleUrgentToggle(s.id)}
                              />
                              <span className="text-xs font-semibold flex items-center gap-1 text-orange-600">
                                <AlertTriangle className="w-3 h-3" />
                                PILNE
                              </span>
                            </label>
                          </div>
                          <Textarea
                            id={`needs-${s.id}`}
                            value={editedNeeds[s.id] ?? s.current_needs ?? ""}
                            onChange={(e) => handleNeedsChange(s.id, e.target.value)}
                            className="min-h-[100px] md:min-h-[180px] resize-y text-sm"
                            placeholder="Wpisz potrzeby schroniska..."
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  selectedShelters.map((s) => {
                    const isUrgentActive = urgentOverrides[s.id] !== undefined 
                      ? urgentOverrides[s.id] 
                      : s.urgent_needs;
                    
                    return (
                      <div key={s.id} className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <Label htmlFor={`needs-${s.id}`} className="text-sm font-semibold">
                            Edytuj potrzeby:
                          </Label>
                          <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                            <Checkbox
                              checked={isUrgentActive ?? false}
                              onCheckedChange={() => handleUrgentToggle(s.id)}
                            />
                            <span className="text-xs font-semibold flex items-center gap-1 text-orange-600">
                              <AlertTriangle className="w-3 h-3" />
                              PILNE
                            </span>
                          </label>
                        </div>
                        <Textarea
                          id={`needs-${s.id}`}
                          value={editedNeeds[s.id] ?? s.current_needs ?? ""}
                          onChange={(e) => handleNeedsChange(s.id, e.target.value)}
                          className="min-h-[100px] md:min-h-[180px] resize-y text-sm"
                          placeholder="Wpisz potrzeby schroniska..."
                        />
                      </div>
                    );
                  })
                )}
                
                {/* Formatting tip - shown once */}
                
                {/* Font size selector - after needs editing, only for non-compact modes */}
                {sizeMode !== "compact" && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Rozmiar czcionki potrzeb:</Label>
                    <div className="flex gap-1.5 flex-wrap">
                      <Button
                        variant={needsFontSize === "small" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNeedsFontSize("small")}
                        className="text-xs px-3"
                      >
                        Mała
                      </Button>
                      <Button
                        variant={needsFontSize === "medium" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNeedsFontSize("medium")}
                        className="text-xs px-3"
                      >
                        Średnia
                      </Button>
                      <Button
                        variant={needsFontSize === "large" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNeedsFontSize("large")}
                        className="text-xs px-3"
                      >
                        Duża
                      </Button>
                    </div>
                  </div>
                )}
                

                <p className="text-xs text-muted-foreground">
                  💡 Użyj <code className="bg-muted px-1 rounded text-foreground">-</code> dla punktów, <code className="bg-muted px-1 rounded text-foreground">**tekst**</code> dla pogrubienia. Maks. {selectedShelters.length === 1 ? "450" : "250"} znaków.
                </p>
                
                {/* Organization disclaimer toggle */}
                <label className="flex items-start gap-2.5 cursor-pointer p-3 bg-muted/30 rounded-lg border border-muted">
                  <Checkbox
                    checked={hideDisclaimer}
                    onCheckedChange={(checked) => setHideDisclaimer(checked === true)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground leading-snug">
                    Udostępniasz jako organizacja? Ukryj disclaimer na grafice.
                  </span>
                </label>
              </div>

              {/* Info Text */}
              <p className="text-sm text-muted-foreground text-center">
                Pobierz grafikę i wrzuć na Facebooka/Instagrama
                <br />
                — udostępnienie zwiększa zasięg.
              </p>
              <div className="flex justify-center">
                <FAQModal>
                  <button className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors">
                    <HelpCircle className="w-3 h-3" />
                    Jak działa generator grafik?
                  </button>
                </FAQModal>
              </div>
            </div>
          </>
        )}
      </DialogContent>

      {/* iOS Save Dialog */}
      <Dialog
        open={!!iosSave}
        onOpenChange={(open) => {
          if (!open) {
            if (iosSave?.blobUrl) URL.revokeObjectURL(iosSave.blobUrl);
            setIosSave(null);
          }
        }}
      >
        <DialogContent className="max-w-md z-[2205]">
          <DialogHeader>
            <DialogTitle>Zapisz grafikę</DialogTitle>
            <DialogDescription>
              {isAndroidDevice 
                ? 'Przytrzymaj podgląd grafiki i wybierz „Pobierz obraz".'
                : 'Przytrzymaj podgląd grafiki i wybierz „Zapisz w Zdjęciach".'}
            </DialogDescription>
          </DialogHeader>

          {iosSave && (
            <div className="rounded-lg overflow-hidden border bg-background">
              <img
                src={iosSave.blobUrl}
                alt={`Podgląd ${iosSave.filename}`}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
